import { DatePicker, Input, InputNumber, Select, Switch } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import isNil from 'lodash/isNil'
import React, {
  ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { RolodexHistogramFilter } from 'types'

import { usePreexistingEnumSelectOptions } from './ontologyHooks'
import {
  OntologyField,
  OntologyFieldDefinition,
  OntologyFieldType,
} from './ontologyTypes'

type PrimitiveOntologyValue = string | number | boolean | null

type OntologyValueArray = ReadonlyArray<
  PrimitiveOntologyValue | OntologyValueArray | OntologyValueRecord
>

type OntologyValueRecord = {
  readonly [key: string]:
    | PrimitiveOntologyValue
    | OntologyValueArray
    | OntologyValueRecord
    | undefined
}

export type OntologyValueType =
  | PrimitiveOntologyValue
  | OntologyValueArray
  | OntologyValueRecord

export interface OntologyFieldInputProps {
  field?: OntologyField
  fieldDef?: OntologyFieldDefinition
  value?: OntologyValueType
  onChange?: (value?: OntologyValueType) => void
  filters?: readonly RolodexHistogramFilter[]
  allowMultiple?: boolean
  style?: React.CSSProperties
  multiline?: boolean
}

const MULTILINE_AUTO_SIZE = { minRows: 3 }

const NumberFieldInput: FC<OntologyFieldInputProps> = ({
  value,
  onChange,
  style,
}) => {
  const handleChange = useCallback(
    (val: number | null) => {
      onChange?.(val === null ? undefined : val)
    },
    [onChange]
  )

  return (
    <InputNumber
      value={typeof value === 'number' ? value : undefined}
      style={style}
      onChange={handleChange}
      readOnly={!onChange}
    />
  )
}

const TextFieldInput: FC<OntologyFieldInputProps> = ({
  value,
  onChange,
  style,
  multiline,
}) => {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange?.(e.target.value)
    },
    [onChange]
  )

  const sharedProps = {
    value: typeof value === 'string' ? value : '',
    style,
    onChange: handleChange,
    readOnly: !onChange,
  }

  return multiline ? (
    <Input.TextArea {...sharedProps} autoSize={MULTILINE_AUTO_SIZE} />
  ) : (
    <Input {...sharedProps} />
  )
}

const EnumFieldInput: FC<OntologyFieldInputProps> = ({
  field,
  fieldDef,
  value,
  onChange,
  filters,
  allowMultiple = false,
  style,
}) => {
  const allowedValues = fieldDef?.allowedValues
  const { enumOptions: preexistingOptions, isLoadingEnumValues } =
    usePreexistingEnumSelectOptions(
      fieldDef?.type ?? 'text',
      allowedValues ? undefined : field,
      filters
    )
  const enumOptions = useMemo(
    () =>
      allowedValues
        ? allowedValues.map((value) => ({ value, label: value }))
        : preexistingOptions,
    [allowedValues, preexistingOptions]
  )

  // NB: can't be a readonly array because of the Select component
  const selectValue: string | Array<string> | undefined = useMemo(() => {
    if (isNil(value)) return allowMultiple ? [] : undefined
    if (allowMultiple)
      return Array.isArray(value) ? value.map(String) : [String(value)]
    return String(value)
  }, [allowMultiple, value])

  const selectedValues = useMemo(
    () => (Array.isArray(value) ? (value as ReadonlyArray<string>) : [value]),
    [value]
  )

  useEffect(() => {
    if (allowedValues || !enumOptions?.length) return
    const optionValues = enumOptions.map((option) => option.value)
    const areAllValuesValid = selectedValues.every((v) =>
      v ? optionValues.includes(v.toString()) : false
    )
    if (!areAllValuesValid) {
      onChange?.(allowMultiple ? [] : undefined)
    }
  }, [allowedValues, enumOptions, onChange, selectedValues, allowMultiple])

  const handleChange = useCallback(
    (newSelected: string | string[]) => {
      if (allowMultiple) {
        onChange?.(newSelected)
      } else {
        onChange?.(Array.isArray(newSelected) ? newSelected[0] : newSelected)
      }
    },
    [onChange, allowMultiple]
  )

  return onChange && enumOptions?.length ? (
    <Select
      mode={allowMultiple ? 'multiple' : undefined}
      loading={!allowedValues && isLoadingEnumValues}
      disabled={!enumOptions?.length}
      value={selectValue}
      style={{ width: '100%', ...style }}
      allowClear
      onChange={handleChange}
      options={enumOptions}
    />
  ) : (
    <TextFieldInput
      value={value}
      onChange={onChange}
      field={field}
      fieldDef={fieldDef}
    />
  )
}

const DateFieldInput: FC<OntologyFieldInputProps> = ({
  value,
  onChange,
  style,
}) => {
  const dateValue = useMemo(
    () => (value ? dayjs(value.toString()) : undefined),
    [value]
  )

  const handleDateChange = useCallback(
    (date: Dayjs | null) => {
      if (!date) {
        onChange?.(undefined)
        return
      }
      onChange?.(date.format('YYYY-MM-DD'))
    },
    [onChange]
  )

  return (
    <DatePicker
      onChange={handleDateChange}
      value={dateValue}
      style={style}
      readOnly={!onChange}
    />
  )
}

const BooleanFieldInput: FC<OntologyFieldInputProps> = ({
  value,
  onChange,
  style,
}) => (
  <Switch
    checked={typeof value === 'boolean' ? value : false}
    onChange={onChange ? (checked) => onChange?.(checked) : undefined}
    disabled={!onChange}
    checkedChildren='Yes'
    unCheckedChildren='No'
    style={style}
  />
)

export const ONTOLOGY_FIELD_INPUTS: Record<
  OntologyFieldType,
  FC<OntologyFieldInputProps>
> = {
  text: memo(TextFieldInput),
  number: memo(NumberFieldInput),
  enum: memo(EnumFieldInput),
  boolean: memo(BooleanFieldInput),
  date: memo(DateFieldInput),
  object: () => <></>,
  array: () => <></>,
}
